import { useState } from 'react';
import { Collapse, Form, FormInstance, Modal } from 'antd';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import { APIFilters } from './APIFilters';
import {
  DataFeedFieldTypes,
  DataFeedFirmographicsFilters,
  DataFeedForm,
  DataFeedFormField,
  SupportedDataset,
} from './DataFeedForm';
import { MultiSearchFilters } from './MultiSearchFilters';
import { JSONRequestFilters } from './JSONRequestFilters';
import './FiltersTab.css';

const hasValue = (field: unknown): boolean => {
  if (field === null || field === undefined || field === false || field === '')
    return false;
  if (typeof field === 'object') return Object.values(field).some(hasValue); // arrays are objects in typescript
  return true;
};

type Filters = DataFeedFieldTypes['filters'];

const datasetHasContent = (
  filters: Filters,
  dataset: SupportedDataset
): boolean =>
  Object.keys(filters[dataset] ?? {}).some((prop) => {
    if (prop === DataFeedFirmographicsFilters.entityType) return false; // doesn't count as a filter on its own
    return hasValue((filters[dataset] as Record<string, unknown>)[prop]);
  });

const urlPath = [DataFeedFormField.filters, 'searchURL'];
const uuidPath = [DataFeedFormField.filters, 'searchConfig', 'uuid'];
const topNOrgsPath = [DataFeedFormField.filters, 'searchConfig', 'topNOrgs'];
const jsonRequestPath = [DataFeedFormField.filters, 'jsonRequest'];

export const filtersCanContinue = (form: FormInstance) => {
  const filters: Filters | undefined = form.getFieldValue(
    DataFeedFormField.filters
  );
  const dataset: SupportedDataset | undefined = form.getFieldValue(
    DataFeedFormField.dataset
  );

  if (!filters) return false;

  if (filters.jsonRequest !== undefined) {
    const isValidating = form.isFieldValidating(jsonRequestPath);
    const hasErrors = form
      .getFieldsError([jsonRequestPath])
      .some((f) => f.errors.length > 0);

    return !isValidating && !hasErrors && !!filters.jsonRequest;
  }

  if (filters.searchConfig !== undefined) {
    const isValidating = form.isFieldValidating(uuidPath);
    const hasErrors = form
      .getFieldsError([urlPath, uuidPath, topNOrgsPath])
      .some((f) => f.errors.length > 0);

    return (
      !isValidating &&
      !hasErrors &&
      !!filters.searchConfig?.tab &&
      !!filters.searchConfig?.uuid
    );
  }

  if (!dataset || filters[dataset] === undefined) return false;
  if (!datasetHasContent(filters, dataset)) return false;

  const datasetFields = Object.keys(filters[dataset] ?? {}).map((prop) => [
    DataFeedFormField.filters,
    dataset,
    prop,
  ]);
  return !form.getFieldsError(datasetFields).some((f) => f.errors.length > 0);
};

const hasContent = (
  filters: Filters | undefined,
  dataset: SupportedDataset | undefined
): boolean => {
  if (!filters) return false;
  if (filters.jsonRequest !== undefined) return !!filters.jsonRequest;
  if (filters.searchConfig !== undefined)
    return !!filters.searchConfig.tab || !!filters.searchConfig.uuid;
  if (!dataset || filters[dataset] === undefined) return false;
  return datasetHasContent(filters, dataset);
};

export const FiltersTab = () => {
  const form = Form.useFormInstance<DataFeedForm>();
  const [activePanel, setActivePanel] = useState<string>('1');

  const onChange = (key: string | string[]) => {
    const next = Array.isArray(key) ? key[0] : key;
    if (!next) return;

    const switchPanel = () => {
      setActivePanel(next);
      form.setFieldValue([DataFeedFormField.filters], undefined);
    };

    const filters: Filters | undefined = form.getFieldValue([
      DataFeedFormField.filters,
    ]);
    const dataset: SupportedDataset | undefined = form.getFieldValue(
      DataFeedFormField.dataset
    );

    if (hasContent(filters, dataset)) {
      Modal.confirm({
        title: 'Discard unsaved filters changes?',
        content:
          'Switching filters will discard unsaved changes. Do you want to continue?',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: switchPanel,
      });
      return;
    }

    switchPanel();
  };

  return (
    <Collapse
      accordion
      bordered={false}
      activeKey={activePanel}
      onChange={onChange}
      destroyInactivePanel
      className="filters-collapse"
    >
      <CollapsePanel header="API" key="1" showArrow={false}>
        <APIFilters />
      </CollapsePanel>
      <CollapsePanel header="API (JSON)" key="2" showArrow={false}>
        <JSONRequestFilters label="JSON string matching the API definition" />
      </CollapsePanel>
      <CollapsePanel header="Multi Search" key="3" showArrow={false}>
        <MultiSearchFilters />
      </CollapsePanel>
    </Collapse>
  );
};
